import { useEffect, useState } from 'react';
import { ReportSection } from '@/reports/shared/ReportSection/ReportSection';
import {
  AutomobileComplaint,
  AutomobileComplaints,
} from '@/reports/types/vehicles';
import { usePagination } from '@/theme/components/Pagination/hooks/usePagination';
import { SelectOption } from '@/theme/components/Select/Select';
import { Pagination, Select, Text } from '@/theme/index';
import { pluralizeString } from '@/utils/pluralizeString';
import { useScreenSize } from '@/utils/useScreenSize';
import { VehicleSafetyComplaintCard } from './VehicleSafetyComplaintCard/VehicleSafetyComplaintCard';

export interface VehicleComplaintsProps {
  /** A list of safety details */
  ymm: string;
  complaints: AutomobileComplaints;
}

function generateComplaintsFilters(complaints: AutomobileComplaints) {
  const complaintsFilters: Record<string, boolean> = {};
  complaints?.forEach((complaint) => {
    if (complaint?.component) {
      complaintsFilters[complaint.component] = true;
    }
  });

  const componentNames = Object.keys(complaintsFilters).sort();
  componentNames.unshift('ALL');
  return (
    componentNames.map((component) => ({
      value: component,
      display: component,
    })) || []
  );
}

/** It renders a view of safety details for the currently selected trim */
export const VehicleSafetyComplaints = ({
  ymm,
  complaints,
}: VehicleComplaintsProps) => {
  const PER_PAGE = 10;
  const { isMobile } = useScreenSize();
  const [complaintsFilters, setComplaintsFilters] = useState<SelectOption[]>(
    []
  );
  const [filterOption, setFilterOption] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const filteredComplaints = filterComplaints(complaints, filterOption);

  useEffect(() => {
    setComplaintsFilters(generateComplaintsFilters(complaints));
  }, [complaints]);

  const totalNumberOfPages = Math.ceil(filteredComplaints.length / PER_PAGE);
  const paginatedData = usePagination([...filteredComplaints], PER_PAGE);

  const handlePageChange = (e: React.ChangeEvent<unknown>, page: number) => {
    jumpToPage(page);
  };

  const jumpToPage = (page: number) => {
    setPage(page);
    paginatedData.jump(page);
  };

  function onFilterSelect(selectedOption: string) {
    setFilterOption(selectedOption === 'ALL' ? null : selectedOption);
    jumpToPage(1);
  }

  function filterComplaints(
    complaints: AutomobileComplaints,
    filterOption: string | null
  ) {
    return complaints
      ? filterOption
        ? complaints.filter((complaint) => {
            return complaint.component === filterOption;
          })
        : complaints
      : [];
  }

  return complaints?.length ? (
    <ReportSection
      showTopBar
      title="NHTSA Safety Complaints"
      hasContent={complaints.length > 0}
      sectionId="vehicle-safety-complaints"
      data-testid="vehicle-safety-complaints"
    >
      <Text variant="body1" data-testid="vehicle-safety-complaints-subheader">
        Below are safety complaints for <strong>{ymm}</strong> submitted by
        consumers to the NHTSA
      </Text>
      <Text variant="body1" data-testid="vehicle-safety-complaints-select-text">
        Filter {complaints.length}{' '}
        {pluralizeString('complaint', complaints.length)} by
      </Text>
      {complaintsFilters.length && (
        <Select
          id="vehicle-safety-trim-selector"
          defaultValue={complaintsFilters ? complaintsFilters[0].value : null}
          options={complaintsFilters}
          onOptionSelect={onFilterSelect}
          data-testid="vehicle-safety-trim-selector"
        />
      )}
      {paginatedData.currentData().map((paginatedItem, paginatedItemIndex) => {
        return (
          <VehicleSafetyComplaintCard
            key={paginatedItemIndex}
            complaint={paginatedItem as AutomobileComplaint}
          />
        );
      })}
      <Pagination
        count={totalNumberOfPages}
        page={page}
        shape="circular"
        onChange={handlePageChange}
        showFirstButton
        showLastButton
        size={isMobile ? 'small' : 'medium'}
        siblingCount={isMobile ? 0 : 2}
        sx={{
          '&.MuiPagination-root': {
            display: 'flex',
            justifyContent: 'center',
          },
        }}
      />
    </ReportSection>
  ) : null;
};
